/**
 * Product analytics tracker — launch-readiness item 2 (2026-09-18).
 *
 * Self-hosted, privacy-light usage analytics (no third-party services).
 * Design:
 *  - Batched queue, flushed every 5s (and on page hide/unload via sendBeacon).
 *  - Payload carries ONLY: event name (snake_case), coarse page path,
 *    coarse device word ('mobile'|'desktop'|'tablet'|'other', once per batch)
 *    and a coarse condo/home lens flag. NO email/name/role/IP/UA in payload.
 *  - User identity: the logged-in user's id, read from the existing auth
 *    storage (same keys AuthContext/api.ts use). NO email/name/role/token in
 *    the payload. Anonymous traffic sends no identity at all.
 *  - Best-effort: every failure is swallowed; analytics must never break UX.
 *
 * Event naming convention: `<module>_<object>_<action>` in snake_case, e.g.
 * `tasks_task_created`. Page views use `page_view` with `page` = route path.
 */

const FLUSH_MS = 5000;
const MAX_QUEUE = 200;

const API_URL =
  (import.meta as any).env?.VITE_API_URL || 'http://localhost:3002/api';
const ENDPOINT = `${API_URL}/analytics/events`;

interface AnalyticsEvent {
  event: string;
  page?: string;
  /** Coarse UI lens: 'condo' | 'home' | 'other' (no identifiers). */
  condoContext?: string;
}

/** Coarse device class from viewport + touch heuristics. No UA string is
 *  read or sent — just the whitelisted word. */
function detectDevice(): string {
  try {
    const w = window.innerWidth;
    const coarseTouch =
      (navigator as Navigator & { maxTouchPoints?: number }).maxTouchPoints > 1;
    if (coarseTouch && w < 480) return 'mobile';
    if (coarseTouch && w < 1024) return 'tablet';
    if (!coarseTouch && w >= 1024) return 'desktop';
    return 'other';
  } catch {
    return 'other';
  }
}

let cachedDevice: string | null = null;
function currentDevice(): string {
  if (!cachedDevice) cachedDevice = detectDevice();
  return cachedDevice;
}

/** Current shell lens ('building' -> 'condo', else 'home'). Coarse flag only. */
function currentCondoContext(): string {
  try {
    return localStorage.getItem('vecindus_lens') === 'building' ? 'condo' : 'home';
  } catch {
    return 'other';
  }
}

const EVENT_NAME_RE = /^[a-z][a-z0-9_]{0,63}$/;

let queue: AnalyticsEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let initialized = false;

/** Read the current logged-in user id from storage (id only — never PII). */
function currentUserId(): string | null {
  try {
    const raw = localStorage.getItem('user');
    if (raw) {
      const user = JSON.parse(raw);
      if (user && typeof user.id === 'string' && user.id) return user.id;
    }
    const stored = JSON.parse(localStorage.getItem('vecindus_auth') || '{}');
    if (stored?.user && typeof stored.user.id === 'string' && stored.user.id) {
      return stored.user.id;
    }
  } catch {
    // ignore parse errors
  }
  return null;
}

function authHeaders(): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  try {
    let token = localStorage.getItem('token');
    if (!token) {
      const stored = JSON.parse(localStorage.getItem('vecindus') || '{}');
      token = stored.token || '';
    }
    if (token) headers.Authorization = `Bearer ${token}`;
  } catch {
    // ignore
  }
  return headers;
}

function safePage(): string | undefined {
  try {
    const p = window.location.pathname;
    return p && p.length <= 128 ? p : undefined;
  } catch {
    return undefined;
  }
}

/** Track a named feature event, e.g. track('tasks_task_created'). */
export function track(event: string): void {
  if (typeof window === 'undefined') return;
  if (!EVENT_NAME_RE.test(event)) return; // fail silent, never throw
  if (queue.length >= MAX_QUEUE) queue.shift(); // cap memory, drop oldest
  queue.push({ event, page: safePage(), condoContext: currentCondoContext() });
}

/** Explicit page view (also fired automatically by the route watcher). */
export function trackPageView(page?: string): void {
  track('page_view');
  void page; // page path is captured from location, not caller input
}

function flush(useBeacon = false): void {
  if (!queue.length) return;
  const batch = queue.splice(0, queue.length);
  const body = JSON.stringify({ events: batch, device: currentDevice() });
  try {
    if (useBeacon && typeof navigator.sendBeacon === 'function') {
      navigator.sendBeacon(
        ENDPOINT,
        new Blob([body], { type: 'application/json' })
      );
      return;
    }
    fetch(ENDPOINT, {
      method: 'POST',
      headers: authHeaders(),
      body,
      keepalive: true,
    }).catch(() => {
      // best-effort: requeue silently (bounded)
      if (queue.length < MAX_QUEUE) queue.unshift(...batch);
    });
  } catch {
    // swallow — analytics must never break UX
  }
}

export function scheduleFlush(): void {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flush();
  }, FLUSH_MS);
}

/**
 * Initialize global instrumentation: page views on SPA navigation, flush on
 * hide/unload. Idempotent; safe to call once at app mount.
 */
export function initAnalytics(): void {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;

  // SPA route changes (pushState/replaceState + back/forward)
  const wrap = (type: 'pushState' | 'replaceState') => {
    const orig = history[type].bind(history) as (
      data: any,
      unused: string,
      url?: string | URL | null
    ) => void;
    history[type] = ((data: any, unused: string, url?: string | URL | null) => {
      orig(data, unused, url);
      trackPageView();
    }) as unknown as History['pushState'];
  };
  wrap('pushState');
  wrap('replaceState');
  window.addEventListener('popstate', () => trackPageView());

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush(true);
  });
  window.addEventListener('pagehide', () => flush(true));

  scheduleFlush();
}

export const analytics = { track, trackPageView, initAnalytics };
